import React, { useEffect } from "react";
import { connect } from "react-redux";
import {
  fetchEnrichedFarmCategories,
  fetchFarmPastures,
  fetchFarmRetreats,
  fetchLivestockInfosFromFarm,
  fetchLotGoals,
  fetchLotWeightGainProjection,
  updateLots,
} from "redux/operations";
import type { RootState } from "redux/reducers";
import toggleUtils from "shared/toggleUtils";
import type { LotsPermissions } from "../lots.types";
import LotsPage from "./lotsPage";

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
type LotsContainerProps = StateProps & DispatchProps;

function LotsContainer({
  enrichedFarmCategories,
  farmRetreats,
  fetchEnrichedFarmCategoriesDispatch,
  fetchFarmPasturesDispatch,
  fetchFarmRetreatsDispatch,
  fetchLivestockInfosFromFarmDispatch,
  fetchLotGoalsDispatch,
  fetchLotWeightGainProjectionDispatch,
  livestockInfosFromFarm,
  lotGoals,
  lotWeightGainProjection,
  toggles,
  updateLotsDispatch,
}: LotsContainerProps) {
  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([
        fetchEnrichedFarmCategoriesDispatch(),
        fetchLotGoalsDispatch(false),
        fetchFarmPasturesDispatch(),
        fetchFarmRetreatsDispatch(),
      ]);

      // Make sure category, goals and pastures are fetched before fetching livestock infos
      fetchLivestockInfosFromFarmDispatch();
    };

    fetchData();
    // We want to run this only once on component mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFetchLotWeightGainProjection = async (
    ...params: Parameters<typeof fetchLotWeightGainProjection>
  ) => {
    await Promise.resolve(fetchLotWeightGainProjectionDispatch(...params));
  };

  const handleUpdateLots = async (...params: Parameters<typeof updateLots>) => {
    await Promise.resolve(updateLotsDispatch(...params));

    // Refetch livestock infos to get the latest data after updating lots
    fetchLivestockInfosFromFarm();
  };

  const permissions: LotsPermissions = {
    showCategorySwap: toggleUtils.isCategorySwapActive(toggles),
    showWeightProjectionColumn:
      toggleUtils.isProjectedWeightColumnActive(toggles),
    showSanityColumn: toggleUtils.isSanityCollectionActive(toggles),
  };

  return (
    <LotsPage
      enrichedAnimalCategories={enrichedFarmCategories}
      farmRetreats={farmRetreats}
      fetchWeightGainProjection={handleFetchLotWeightGainProjection}
      livestockInfosFromFarm={livestockInfosFromFarm}
      lotGoals={lotGoals}
      lotsPermissions={permissions}
      updateLots={handleUpdateLots}
      weightGainProjection={lotWeightGainProjection}
    />
  );
}

const mapStateToProps = (state: RootState) => ({
  enrichedFarmCategories: state.category.enrichedFarmCategories,
  farmRetreats: state.pasture.farmRetreats,
  livestockInfosFromFarm: state.livestock.infosFromFarm,
  lotGoals: state.lot.goals,
  lotWeightGainProjection: state.lot.lotWeightProjection,
  toggles: state.fazenda.Toggles,
});

const mapDispatchToProps = {
  fetchEnrichedFarmCategoriesDispatch: fetchEnrichedFarmCategories,
  fetchLotGoalsDispatch: fetchLotGoals,
  fetchFarmPasturesDispatch: fetchFarmPastures,
  fetchFarmRetreatsDispatch: fetchFarmRetreats,
  fetchLivestockInfosFromFarmDispatch: fetchLivestockInfosFromFarm,
  fetchLotWeightGainProjectionDispatch: fetchLotWeightGainProjection,
  updateLotsDispatch: updateLots,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(LotsContainer);
